import { ScatterplotLayer } from '@deck.gl/layers';
import maplibregl from 'maplibre-gl';
import type { Scene, LandmarkData, MapAdapter } from '../types';
import { RadialChart } from '../components/RadialChart';
import { PALETTE } from '../palette';
import { hexToRgba } from '../colorUtils';

const COLORS = [PALETTE.sand, PALETTE.blue, PALETTE.green] as const;
const ICONS   = ['◎', '~', '♦'] as const;

interface LandmarkDatum {
  position: [number, number];
  color: [number, number, number, number];
  name: string;
  idx: number;
}

export class SceneLandmarks implements Scene {
  readonly panel: HTMLElement;
  private radials: RadialChart[] = [];
  private scatterLayer: ScatterplotLayer<LandmarkDatum> | null = null;
  private labelMarkers: maplibregl.Marker[] = [];
  private rafId: number | null = null;
  private pulseT = 0;
  private built = false;

  private readonly pairSim: number[][];

  constructor(
    root: HTMLElement,
    private readonly landmarks: LandmarkData[],
    private readonly map: MapAdapter,
  ) {
    this.pairSim = landmarks.map(a =>
      landmarks.map(b =>
        a.embed.reduce((s, v, i) => s + v * b.embed[i], 0),
      ),
    );

    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'landmarks';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Embedding Landmarks</div>
        <div class="sm-panel-sub">
          Three contrasting environments · AlphaEarth 2024 · cosine metric
        </div>
      </div>
      <div class="sm-headline">
        Two environments that look similar to the eye carry completely different
        satellite fingerprints — a precision that lets us map habitat type
        across the entire concession without visiting every plot.
      </div>
      <div class="sm-lm-body" id="sm-lm-body"></div>
      <div class="sm-lm-sim-table" id="sm-lm-sim-table"></div>`;
    root.appendChild(this.panel);
  }

  onEnter(): void {
    if (!this.built) {
      this.build();
      this.built = true;
    }
    this.placeMarkers();
    this.radials.forEach((r, i) => r.show(this.landmarks[i].embed));
  }

  onLeave(): void {
    this.radials.forEach(r => r.stop());
    this.removeMarkers();
  }

  private build(): void {
    const body = this.panel.querySelector<HTMLElement>('#sm-lm-body')!;

    this.landmarks.forEach((lm, i) => {
      const col = COLORS[i];
      const col_div = document.createElement('div');
      col_div.className = 'sm-lm-col';

      col_div.innerHTML = `
        <div class="sm-lm-col-name" style="color:${col};">
          <span class="sm-lm-icon">${ICONS[i]}</span> ${lm.name}
        </div>
        <div class="sm-lm-coord">${lm.lat.toFixed(4)}°, ${lm.lon.toFixed(4)}°</div>
        <div class="sm-lm-radial-wrap" id="sm-lm-r-${i}"></div>`;

      body.appendChild(col_div);

      const wrap = col_div.querySelector<HTMLElement>(`#sm-lm-r-${i}`)!;
      this.radials.push(new RadialChart(wrap, 190, 170));
    });

    this.buildSimTable();
  }

  private buildSimTable(): void {
    const el = this.panel.querySelector<HTMLElement>('#sm-lm-sim-table')!;
    const N = this.landmarks.length;
    const pairs: { a: number; b: number; sim: number }[] = [];
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        pairs.push({ a: i, b: j, sim: this.pairSim[i][j] });
      }
    }
    pairs.sort((a, b) => b.sim - a.sim);

    el.innerHTML = `
      <div class="sm-lm-sim-header">PAIRWISE COSINE SIMILARITY</div>
      ${pairs.map(p => {
        const t = (p.sim + 1) / 2;
        const barW = Math.round(t * 100);
        const colA = COLORS[p.a];
        const colB = COLORS[p.b];
        return `
          <div class="sm-lm-sim-row">
            <span class="sm-lm-sim-label">
              <span style="color:${colA};">${ICONS[p.a]}</span>
              &nbsp;vs&nbsp;
              <span style="color:${colB};">${ICONS[p.b]}</span>
            </span>
            <div class="sm-lm-sim-bar">
              <div class="sm-lm-sim-fill" style="width:${barW}%;"></div>
            </div>
            <span class="sm-lm-sim-val">${p.sim.toFixed(3)}</span>
          </div>`;
      }).join('')}`;
  }

  private placeMarkers(): void {
    const mlMap = (this.map as unknown as { map: maplibregl.Map }).map;

    const lmData: LandmarkDatum[] = this.landmarks.map((lm, i) => ({
      position: [lm.lon, lm.lat] as [number, number],
      color: hexToRgba(COLORS[i]),
      name: lm.name,
      idx: i,
    }));

    // HTML labels via MapLibre Marker (always readable, no texture atlas needed)
    this.landmarks.forEach((lm, i) => {
      const el = document.createElement('div');
      el.className = 'sm-lm-tooltip';
      el.textContent = lm.name;
      el.style.cssText = `pointer-events:none;color:${COLORS[i]};white-space:nowrap;`;
      const marker = new maplibregl.Marker({ element: el, anchor: 'left', offset: [14, 0] })
        .setLngLat([lm.lon, lm.lat])
        .addTo(mlMap);
      this.labelMarkers.push(marker);
    });

    // Pulsing scatter layer — added once; updated each rAF via addLayer (same id = replace)
    const animate = () => {
      this.pulseT += 0.05;
      const t = this.pulseT;
      this.scatterLayer = new ScatterplotLayer<LandmarkDatum>({
        id: 'landmarks-scatter',
        data: lmData,
        getPosition: d => d.position,
        getFillColor: d => d.color,
        getRadius: d => 9 + Math.sin(t + d.idx * 2) * 4,
        radiusUnits: 'pixels',
        opacity: 0.9,
        stroked: true,
        getLineColor: [255, 255, 255, 60],
        lineWidthMinPixels: 1,
      });
      this.map.addLayer(this.scatterLayer);
      this.rafId = requestAnimationFrame(animate);
    };
    this.rafId = requestAnimationFrame(animate);
  }

  private removeMarkers(): void {
    if (this.rafId !== null) { cancelAnimationFrame(this.rafId); this.rafId = null; }
    if (this.scatterLayer) { this.map.removeLayer(this.scatterLayer); this.scatterLayer = null; }
    this.labelMarkers.forEach(m => m.remove());
    this.labelMarkers = [];
  }
}
